import * as THREE from "three";
import { HDRLoader } from "three/examples/jsm/Addons.js";
import type { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import EventEmitter from "./EventEmitter";

interface GltfSource {
  name: string;
  type: "gltfModel";
  path: string;
}

interface TextureSource {
  name: string;
  type: "texture";
  path: string;
}

interface CubeTextureSource {
  name: string;
  type: "cubeTexture";
  path: string[];
}

interface HdrSource {
  name: string;
  type: "hdr";
  path: string;
}

export type Source = GltfSource | TextureSource | CubeTextureSource | HdrSource;

type LoadedItem = GLTF | THREE.Texture | THREE.CubeTexture;

interface Loaders {
  gltfLoader: GLTFLoader;
  textureLoader: THREE.TextureLoader;
  cubeTextureLoader: THREE.CubeTextureLoader;
  hdrLoader: HDRLoader;
}

type ItemsMap = Record<string, LoadedItem>;

export default class Resources extends EventEmitter {
  private sources: Source[];
  public readonly items: ItemsMap = {};
  private toLoad: number;
  private loaded = 0;
  private loaders: Loaders;

  constructor(sources: Source[]) {
    super();

    this.sources = sources;
    this.toLoad = this.sources.length;

    this.loaders = {
      gltfLoader: new GLTFLoader(),
      textureLoader: new THREE.TextureLoader(),
      cubeTextureLoader: new THREE.CubeTextureLoader(),
      hdrLoader: new HDRLoader(),
    };
    this.startLoading();
  }

  private startLoading() {
    for (const source of this.sources) {
      switch (source.type) {
        case "gltfModel":
          this.loaders.gltfLoader.load(source.path, (file) => {
            this.sourceLoaded(source, file);
          });
          break;
        case "texture":
          this.loaders.textureLoader.load(source.path, (file) => {
            this.sourceLoaded(source, file);
          });
          break;
        case "cubeTexture":
          this.loaders.cubeTextureLoader.load(source.path, (file) => {
            this.sourceLoaded(source, file);
          });
          break;
        case "hdr":
          this.loaders.hdrLoader.load(source.path, (file) => {
            this.sourceLoaded(source, file);
          });
          break;
        default:
          break;
      }
    }
  }

  private sourceLoaded(source: Source, file: LoadedItem) {
    this.items[source.name] = file;
    this.loaded += 1;

    if (this.loaded === this.toLoad) {
      this.trigger("ready");
    }
  }

  get<ResourceType>(name: string): ResourceType | undefined {
    return this.items[name] as ResourceType;
  }
}
